import { ButtonUtils, ContextUtils, FontUtils, GridUtils, Image3D, ImgButton, InputUtils, MeshUtils, ObjectBuilder, Rectangle3D, ScrollViewer3D } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { Container } from "@babylonjs/gui/2D/controls/container.js";
import { SubmenuHandlers } from "./SubmenuHandlers";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { text } from "../../TextResources";
import { BroadcastApiStatus, BroadcastManagerState } from "@dreamcam/web-sdk";
import { LayoutComponentHelper } from "./LayoutComponentHelper";
import { DateUtils } from "../../utils/Time";
import { Keys } from "./keyboard/Keyboard3D";
import { DcRenderingGroup } from "../DcRenderingGroup";
import { XRInputHelper } from "../XRInputHelper";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { AppView } from "../AppView";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { ScrollHandler } from "../ScrollHandler";
import { MessengerLayoutHelper } from "./MessengerLayoutHelper";
import { AvatarStatus } from "./AvatarStatus";
import { MessengerPanelData } from "./MessengerPanelData";
import { LinkWarningDialog } from "./LinkWarningDialog";
import { globalOptions } from "../../settings/GlobalOptions";
import { Settings } from "../../settings/Settings";
import { BroadcastMenuTipsKeyboard } from "./BroadcastMenuTipsKeyboard";
import { MenuPopupContainer } from "./MenuPopupContainer";
import { TextWrapping } from "@babylonjs/gui/2D/controls/textBlock.js";
const MARK_AS_READ_DELAY = 2_000;
class ChatScrollViewer extends ScrollViewer3D {
    _postMeasure() {
        // @ts-ignore
        super._postMeasure();
        // @ts-ignore
        this._updateScroller();
    }
}
export class MessengerPanel {
    get linkWarningDialog() {
        return this._linkWarningDialog;
    }
    get tipsKeyboardMesh2d() {
        return this._tipsKeyboardMesh2d;
    }
    get isChatListButtonOn() {
        return this._isChatListButtonOn;
    }
    set isChatListButtonOn(value) {
        if (value === this._isChatListButtonOn)
            return;
        const cb = Env.common.messengerPanel.header.chatListButton;
        this._controls.header.chatListButton.changeLayout(value ? cb.activeImageLayout : cb.inactiveImageLayout);
        this._isChatListButtonOn = value;
    }
    get mesh2d() { return this._mesh2d; }
    constructor(params) {
        this._onHideCallback = null;
        this._modelData = new MessengerPanelData();
        this._allMessages = { chatId: undefined, items: [] };
        this._unreadModelMessages = [];
        this._markAsReadTimeout = null;
        this._renderedDateKeys = new Set();
        this._tipsKeyboard = null;
        this._onHideKeyboardClickObserver = null;
        this._onVerticalValueChangedObserver = null;
        this._newMessagesItem = null;
        this._hasUserMessagesAfterNewMessagesItem = false;
        this._isChatListButtonOn = false;
        this._messagesFirstRenderAfterShow = true;
        this._unreadMessagesIdsBeforeShow = [];
        this._linkClickHandler = (link) => {
            setTimeout(() => {
                this._linkWarningDialog.show(link);
            });
        };
        this._avatars = new Map(); // (key: modelId, value: AvatarImage[])
        this._verticalValueChangedHandler = (value) => {
            if (this.isVisible && this._modelData.chat !== null) {
                this._saveScrollPosition(this._modelData.chat.id);
            }
            this._setJumpDownButtonVisibility(this._modelData.chat?.id ?? null);
            this._tryToMarkUnreadMessages(value);
            this._tryToLoadHistoryMessages(value);
        };
        this._mesh2d = params.mesh2d;
        this._ellipsisSubmenuMesh2d = params.ellipsisSubmenuMesh2d;
        this._keyboard = params.keyboard;
        this._hideKeyboardComponent = params.hideKeyboardComponent;
        this._tipsKeyboardMesh2d = params.tipsKeyboardMesh2d;
        this._callbacks = params.callbacks;
        this._ellipsisSubmenuHandlers = new SubmenuHandlers(this._ellipsisSubmenuMesh2d);
        this._linkWarningDialog = new LinkWarningDialog(this._mesh2d.mesh, params.linkWarningDialogMeshName);
        this._controls = this._createContent(this._mesh2d.texture);
        this._keyboard.isVisible = false;
    }
    static createMessengerPanel(params) {
        const mp = Env.common.messengerPanel;
        const scene = AppView.getInstance().scene;
        const mesh2d = MeshUtils.createPlaneMesh(params.messengerPanelMeshName, mp.background.width, mp.background.height, scene, params.parentNode, DcRenderingGroup.topDialogs);
        mesh2d.mesh.position = params.messengerPanelPosition;
        const pointerAddon = XRInputHelper.addPointerAddon(mesh2d.mesh);
        pointerAddon.onPointerOut.add(() => {
            mesh2d.notifyChildrenOnPointerOut();
        });
        const sm = mp.header.ellipsisSubmenu;
        const ellipsisSubmenuMesh2d = MeshUtils.createPlaneMesh(params.ellipsisSubmenuMeshName, sm.width, sm.height, scene, mesh2d.mesh, DcRenderingGroup.messageDialogs);
        ellipsisSubmenuMesh2d.mesh.position = new Vector3(sm.offset.left, sm.offset.top, sm.offset.distance);
        const submenuPointerAddon = XRInputHelper.addPointerAddon(ellipsisSubmenuMesh2d.mesh);
        submenuPointerAddon.onPointerOut.add(() => ellipsisSubmenuMesh2d.notifyChildrenOnPointerOut());
        const kbd = Env.common.messengerPanel.tips.customTip.tipsKeyboard;
        const tipsKeyboardMesh2d = MeshUtils.createPlaneMesh(params.tipsKeyboardMeshName, kbd.width, kbd.height, scene, mesh2d.mesh, DcRenderingGroup.aboveDialogs); //messageDialogs);
        MeshUtils.hide(tipsKeyboardMesh2d);
        const tipsKeyboardPointerAddon = XRInputHelper.addPointerAddon(tipsKeyboardMesh2d.mesh);
        tipsKeyboardPointerAddon.onPointerOut.add(() => tipsKeyboardMesh2d.notifyChildrenOnPointerOut());
        return new MessengerPanel({
            mesh2d,
            ellipsisSubmenuMesh2d,
            linkWarningDialogMeshName: params.linkWarningDialogMeshName,
            keyboard: params.keyboard,
            hideKeyboardComponent: params.hideKeyboardComponent,
            tipsKeyboardMesh2d,
            callbacks: params.callbacks,
        });
    }
    hide() {
        if (MeshUtils.isHidden(this._mesh2d))
            return;
        setTimeout(() => {
            if (this._markAsReadTimeout !== null) {
                clearTimeout(this._markAsReadTimeout);
                this._markAsReadTimeout = null;
            }
            this.isVisible = false;
            this._ellipsisSubmenuHandlers.hideSubmenuInstantly();
            this._keyboard.isVisible = false;
            this._hideKeyboardComponent.onClickObservable.remove(this._onHideKeyboardClickObserver);
            this._onHideKeyboardClickObserver = null;
            this._controls.chatPanel.scrollHandler.releaseThumbstickAxesChangedListener();
        }, 0);
        if (this._onHideCallback) {
            this._onHideCallback();
            this._onHideCallback = null;
        }
    }
    show(onHideCallback) {
        const appView = AppView.getInstance();
        if (appView.customTip > 0) {
            this._controls.tipsPanel.tipsInputControl.text = `${appView.customTip}`;
        }
        this._callbacks.showChatListPanel();
        this._onHideCallback = onHideCallback;
        this._onHideKeyboardClickObserver = this._hideKeyboardComponent.onClickObservable.add(() => this._controls.editorPanel.inputControl.blur());
        this._controls.chatPanel.scrollHandler.addThumbstickAxesChangedListener();
        this.isVisible = true;
        this._messagesFirstRenderAfterShow = true;
        this._setJumpDownButtonVisibility(null);
        this._messengerDataCallbacks.outputMessages(this._messengerDataCallbacks.getSelectedChatId());
    }
    get isVisible() {
        return this._mesh2d.mesh.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        MeshUtils.setVisibility(this._mesh2d, value);
        if (value) {
            this._keyboard.control.connect(this._controls.editorPanel.inputControl);
        }
        else {
            this._keyboard.control.disconnect(this._controls.editorPanel.inputControl);
            this._linkWarningDialog.hide();
        }
    }
    setPinnedModelId(modelId) {
        this._modelData.setPinnedModelId(modelId);
    }
    outputPinnedModelNickname(modelId, nickname) {
        this._modelData.setPinnedModelId(modelId);
        this._modelData.setPinnedModelNickname(nickname);
        this._updateHeader();
    }
    outputPinnedBroadcastManagerState(state, _isGuest, _reason) {
        this._modelData.setPinnedBroadcastManagerState(state);
        this._updateHeader();
    }
    outputPinnedProfilePhoto(url) {
        this._modelData.setPinnedModelProfileImageUrl(url);
        this._updateHeader();
        const modelId = this._modelData.selectedModelId;
        if (modelId !== null) {
            this._refreshModelAvatar(modelId, url);
        }
    }
    overwriteIsFavorite(modelId, isFavorite) {
        if (modelId === this._modelData.pinnedModelId) {
            this._modelData.setPinnedIsFavorite(isFavorite, true);
        }
    }
    outputPinnedIsFavorite(value) {
        this._modelData.setPinnedIsFavorite(value, false);
        this._updateHeader();
    }
    outputPinnedLastBroadcast(value) {
        this._modelData.setPinnedLastBroadcast(value);
        this._updateHeader();
    }
    _clearChatMessages() {
        // this._controls.chatPanel.stackPanel.clearControls();
        this._controls.chatPanel.stackPanel.dispose();
        this._controls.chatPanel.stackPanel = this._createChatStackPanel();
        this._controls.chatPanel.scrollViewer.addControl(this._controls.chatPanel.stackPanel);
        // this._controls.chatPanel.scrollViewer.markAsDirty(true);
        // this._controls.chatPanel.scrollViewer.resetWindow();
        this._renderedDateKeys.clear();
    }
    _addGap(name, heightInPixels) {
        const gap = new Container(name);
        // gap.background = "#ff000020";
        gap.heightInPixels = heightInPixels;
        this._controls.chatPanel.stackPanel.addControl(gap);
        return gap;
    }
    _addDateLine(date) {
        const messageControls = MessengerLayoutHelper.createDateLine({
            mesh2d: this._mesh2d,
            date,
        });
        this._controls.chatPanel.stackPanel.addControl(messageControls.rootContainer);
    }
    _addNewMessagesLine() {
        const messageControls = MessengerLayoutHelper.createNewMessagesLine({
            mesh2d: this._mesh2d,
        });
        this._controls.chatPanel.stackPanel.addControl(messageControls.rootContainer);
        return messageControls;
    }
    _cloneAvatarImage(modelId, url) {
        if (modelId === null)
            return null;
        const avatarImages = this._avatars.get(modelId) ?? [];
        this._avatars.set(modelId, avatarImages);
        let image;
        if (avatarImages.length === 0) {
            image = new Image3D(`modelMessageAvatar${modelId}`, url);
            avatarImages.push({
                image,
                widthInPixels: undefined,
                heightInPixels: undefined,
            });
            image.onImageLoadedObservable.addOnce((image) => {
                const aspectRatio = image.domImage.width / image.domImage.height;
                if (!image.heightInPixels || image.heightInPixels <= 0) {
                    image.heightInPixels = this._mesh2d.m2px(Env.common.messengerPanel.chat.messageContainer.modelMessage.avatar.height);
                }
                image.widthInPixels = image.heightInPixels * aspectRatio;
                const modelImages = this._avatars.get(modelId);
                modelImages?.forEach(avatarImage => {
                    avatarImage.widthInPixels = image.widthInPixels;
                    avatarImage.heightInPixels = image.heightInPixels;
                });
                setTimeout(() => {
                    this._controls.chatPanel.stackPanel.markAsDirty();
                    this._controls.chatPanel.scrollViewer.markAsDirty(true);
                    this._controls.rootGrid.markAllAsDirty();
                }, 0);
            });
        }
        else {
            const firstAvatarImage = avatarImages[0];
            image = firstAvatarImage.image.clone();
            if (firstAvatarImage.widthInPixels !== undefined)
                image.widthInPixels = firstAvatarImage.widthInPixels;
            if (firstAvatarImage.heightInPixels !== undefined)
                image.heightInPixels = firstAvatarImage.heightInPixels;
            avatarImages.push({
                image,
                widthInPixels: firstAvatarImage.widthInPixels,
                heightInPixels: firstAvatarImage.heightInPixels,
            });
        }
        return image;
    }
    _clearAvatarImageClones() {
        for (const avatarImages of this._avatars.values()) {
            if (avatarImages.length > 0)
                avatarImages.length = 1;
        }
    }
    _addModelMessage(text, time) {
        const avatarImage = this._cloneAvatarImage(this._modelData.selectedModelId, this._modelData.modelProfileImageUrl);
        const messageControls = MessengerLayoutHelper.createModelMessage({
            mesh2d: this._mesh2d,
            avatarImage,
            text,
            date: time,
            linkClickHandler: this._linkClickHandler,
        });
        this._controls.chatPanel.stackPanel.addControl(messageControls.rootContainer);
        return messageControls;
    }
    _addUserTips(tipsAmount, time) {
        const messageControls = MessengerLayoutHelper.createUserTips({
            mesh2d: this._mesh2d,
            tipsAmount,
            date: time,
        });
        this._controls.chatPanel.stackPanel.addControl(messageControls.rootContainer);
        return messageControls;
    }
    _addUserMessage(text, time, status) {
        const messageControls = MessengerLayoutHelper.createUserMessage({
            mesh2d: this._mesh2d,
            text,
            date: time,
            status,
            linkClickHandler: this._linkClickHandler,
        });
        this._controls.chatPanel.stackPanel.addControl(messageControls.rootContainer);
        return messageControls;
    }
    setMessengerDataCallbacks(callbacks) {
        this._messengerDataCallbacks = callbacks;
    }
    outputMessengerSelectedModel(modelId, chat) {
        if (this.isVisible) {
            const previousChatId = this._modelData.chat?.id ?? null;
            if (chat !== null && chat.id !== previousChatId && !this._messagesFirstRenderAfterShow) {
                this._messagesFirstRenderAfterShow = true;
                this._unreadMessagesIdsBeforeShow = [];
            }
        }
        this._modelData.setSelectedModelId(modelId);
        this._modelData.setSelectedChat(chat);
        this._modelData.isTyping = chat !== null ? this._messengerDataCallbacks.getIsModelTyping(chat.id) : false;
        this._modelData.setBroadcastApiStatus(BroadcastApiStatus.OFFLINE);
        if (modelId !== null) {
            const modelStatus = this._messengerDataCallbacks.getModelStatuses().find(x => x.userId === modelId);
            if (modelStatus !== undefined) {
                this._modelData.setBroadcastApiStatus(modelStatus.status);
                if (this._modelData.chat) {
                    this._modelData.chat.participant.last_seen =
                        modelStatus.lastOnlineDate !== null ? modelStatus.lastOnlineDate.toUTCString() : "";
                }
            }
        }
        this._updateHeader();
        this.setInputControlsAvailability({
            isMuted: chat?.participant.reverse_mute_data.is_muted ?? false,
            isBanned: chat?.participant.reverse_block_data.is_blocked ?? false,
        });
        this._controls.editorPanel.inputControl.text = chat?.draft_message.content ?? "";
        this._controls.chatPanel.stackPanel.clearControls();
        const mode = chat === null || chat.last_message === null ? "warning" : "chat";
        this._setChatPanelMode(mode);
        if (mode === "warning") {
            this.outputMessengerMessages(null, null);
        }
        this._setJumpDownButtonVisibility(chat?.id ?? null);
    }
    outputMessengerModelStatuses(modelStatuses) {
        if (this._modelData.chat !== null) {
            const modelId = this._modelData.chat.participant.user_id;
            const status = modelStatuses.find(x => x.userId === modelId)?.status;
            if (status) {
                this._modelData.setBroadcastApiStatus(status);
                this._updateHeader();
            }
        }
    }
    outputMessengerChatChanged(args) {
        if (args.chatData.id === this._modelData.chat?.id) {
            const oldAvatarUrl = this._modelData.modelProfileImageUrl;
            this._modelData.setSelectedChat(args.chatData);
            this._updateHeader();
            const newAvatarUrl = args.chatData.participant.avatar ?? '';
            if (oldAvatarUrl !== newAvatarUrl && this._modelData.selectedModelId !== null) {
                this._refreshModelAvatar(this._modelData.selectedModelId, newAvatarUrl);
            }
            if (args.event === "MUTE_LIST" || args.event === "BLACK_LIST") {
                this.setInputControlsAvailability({
                    isMuted: args.chatData.participant.reverse_mute_data.is_muted,
                    isBanned: args.chatData.participant.reverse_block_data.is_blocked,
                });
            }
            if (args.event === "DRAFT") {
                this._controls.editorPanel.inputControl.text = args.chatData.draft_message.content ?? "";
            }
        }
    }
    outputMessengerModelTypingChanged(chatId, isTyping) {
        if (chatId === this._modelData.chat?.id) {
            this._modelData.isTyping = isTyping;
            this._updateHeader();
        }
    }
    outputMessengerMessages(chatId, messageListByDate) {
        if (!this.isVisible)
            return;
        this._freezeScrollControl(chatId);
        try {
            // Fallback: full rebuild
            this._clearChatMessages();
            this._controls.chatPanel.topGap = this._addGap("messengerPanelChatTopGap", 1);
            this._controls.chatPanel.bottomGap = null;
            if (chatId === null || messageListByDate === null || chatId !== this._modelData.chat?.id || this._modelData.selectedModelId === null) {
                return;
            }
            const userId = this._messengerDataCallbacks.getUserClientId();
            // const lastMessageSenderIsModel: boolean = allLoadedMessages.at(-1)?.sender_id !== userId;
            if (this._messagesFirstRenderAfterShow) {
                const allLoadedMessages = Array
                    .from(messageListByDate.values())
                    .flatMap((list) => list.toArray());
                this._unreadMessagesIdsBeforeShow = allLoadedMessages
                    .filter(x => x.sender_id !== userId && x.read_at === null)
                    .map(x => x.id);
            }
            this._newMessagesItem = null;
            this._hasUserMessagesAfterNewMessagesItem = false;
            this._allMessages = { chatId, items: [] };
            this._unreadModelMessages = [];
            this._clearAvatarImageClones();
            const dateKeysSorted = Array.from(messageListByDate.keys()).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
            for (const dateString of dateKeysSorted) {
                this._addDateLine(new Date(dateString));
                this._renderedDateKeys.add(dateString);
                const messageList = messageListByDate.get(dateString);
                const messages = messageList ? messageList.toArray() : [];
                messages.sort((m1, m2) => new Date(m1.sent_at).getTime() - new Date(m2.sent_at).getTime());
                for (const message of messages) {
                    if (message.sender_id === userId) {
                        const messageSentAt = new Date(message.sent_at);
                        const messageStatus = message.read_at !== null ? "READ" : "SENT";
                        const tipsAmount = message.attachments.find(x => x.type === "tip")?.meta.amount;
                        const hasTips = tipsAmount !== undefined && tipsAmount > 0;
                        const userMessageControls = hasTips
                            ? this._addUserTips(tipsAmount, messageSentAt)
                            : this._addUserMessage(message.content, messageSentAt, messageStatus);
                        if (this._newMessagesItem !== null)
                            this._hasUserMessagesAfterNewMessagesItem = true;
                        this._allMessages.items.push({
                            chatId,
                            messageId: message.id,
                            sender: "client",
                            // _messageContent: message.content,
                            controls: userMessageControls,
                        });
                    }
                    else {
                        const isNewMessage = this._unreadMessagesIdsBeforeShow.find(x => x === message.id) !== undefined;
                        if (this._newMessagesItem === null && isNewMessage) {
                            this._newMessagesItem = this._addNewMessagesLine();
                        }
                        const messageSentAt = new Date(message.sent_at);
                        const incomingTipAmount = message.attachments.find(x => x.type === "tip")?.meta.amount;
                        const messageText = (message.content && message.content.trim().length > 0)
                            ? message.content
                            : (incomingTipAmount !== undefined && incomingTipAmount > 0 ? `${incomingTipAmount}` : "");
                        const modelMessageControls = this._addModelMessage(messageText, messageSentAt);
                        this._allMessages.items.push({
                            chatId,
                            messageId: message.id,
                            sender: "model",
                            // _messageContent: message.content,
                            controls: modelMessageControls,
                        });
                        if (message.read_at === null) {
                            this._unreadModelMessages.push({
                                chatId,
                                message,
                                controls: modelMessageControls,
                                wasShownToUser: false,
                                wasMarkedAsRead: false,
                            });
                        }
                    }
                }
            }
        }
        finally {
            this._messagesFirstRenderAfterShow = false;
            this._unfreezeScrollControl(chatId);
            this._controls.chatPanel.bottomGap = this._addGap("messengerPanelChatBottomGap", 1);
            const mode = this._allMessages.chatId && this._allMessages.items.length > 0 ? "chat" : "warning";
            this._setChatPanelMode(mode);
        }
    }
    outputMessengerIsFavoriteChanged(modelId, isFavorite) {
        if (modelId !== this._messengerDataCallbacks.getSelectedModelId())
            return;
        this._controls.header.ellipsisSubmenuFavoritesItem.imgButton.isEnabled = true;
        this.outputPinnedIsFavorite(isFavorite);
        this._updateHeader();
    }
    _freezeScrollControl(_chatId) {
        this._controls.chatPanel.scrollViewer.onVerticalValueChangedObservable.remove(this._onVerticalValueChangedObserver);
    }
    _unfreezeScrollControl(chatId) {
        if (chatId === this._allMessages.chatId) {
            // SEE THE ALGORITHM IN /messenger-algorithms.md
            const previousScrollState = chatId === null ? undefined : MessengerPanel._chatScrollPositions.get(chatId);
            const clamp01 = (value) => Math.max(Math.min(value, 1), -1);
            const stackPanelHeight = this._controls.chatPanel.stackPanel.heightInPixels;
            const scrollViewerHeight = this._controls.chatPanel.scrollViewer.heightInPixels;
            const scrollDown = () => {
                let result;
                if (this._newMessagesItem !== null && this._unreadModelMessages.length > 0 && !this._hasUserMessagesAfterNewMessagesItem) {
                    // Set NewMessagesLine position:
                    const newMessageItemTop = this._newMessagesItem.rootContainer.topInPixels;
                    const newMessagesAreaHeight = stackPanelHeight - newMessageItemTop;
                    const deltaHeight = stackPanelHeight - scrollViewerHeight;
                    const scrollWindowTop = newMessagesAreaHeight > scrollViewerHeight ? newMessageItemTop : deltaHeight;
                    result = deltaHeight === 0 ? 0 : clamp01(scrollWindowTop / deltaHeight);
                }
                else {
                    // Scroll down:
                    result = 1;
                }
                return result;
            };
            let newScrollPosition;
            if (previousScrollState === undefined) {
                newScrollPosition = scrollDown();
            }
            else {
                if (previousScrollState.isLastMessageVisibleInScrollViewer) {
                    newScrollPosition = scrollDown();
                }
                else {
                    // Set same first visible element position on the screen:
                    const messageBubble = this._allMessages.items.find(x => x.chatId === chatId && x.messageId === previousScrollState.firstVisibleMessageId);
                    if (messageBubble) {
                        const stackPanelHeight = this._controls.chatPanel.stackPanel.heightInPixels;
                        const scrollWindowHeight = this._controls.chatPanel.scrollViewer.heightInPixels;
                        const scrollWindowTop = messageBubble.controls.rootContainer.topInPixels - previousScrollState.firstVisibleMessageTopPxOnScrollViewer;
                        const deltaHeight = stackPanelHeight - scrollWindowHeight;
                        newScrollPosition = deltaHeight === 0 ? 0 : scrollWindowTop / deltaHeight;
                    }
                    else { // (impossible, just in case)
                        newScrollPosition = 1;
                    }
                }
            }
            if (newScrollPosition === this._controls.chatPanel.scrollViewer.verticalValue) { // (a patch to force scroll update)
                this._controls.chatPanel.scrollViewer.verticalValue = newScrollPosition > 0.01 ? 0 : 0.011;
            }
            this._controls.chatPanel.scrollViewer.verticalValue = newScrollPosition;
            if (chatId !== null) {
                this._saveScrollPosition(chatId);
            }
            // Since the onVerticalValueChanged handler has been removed, set jumpDownButton visibility manually:
            this._setJumpDownButtonVisibility(chatId);
            this._tryToMarkUnreadMessages(newScrollPosition);
        }
        this._onVerticalValueChangedObserver =
            this._controls.chatPanel.scrollViewer.onVerticalValueChangedObservable.add(this._verticalValueChangedHandler);
    }
    _setJumpDownButtonVisibility(chatId) {
        this._controls.jumpDownControls.rootContainer.isVisible =
            chatId !== null
                && this._allMessages.chatId === chatId
                && this._allMessages.items.length > 0
                && this._controls.chatPanel.rootContainer.isVisible
                && this._controls.chatPanel.stackPanel.heightInPixels > this._controls.chatPanel.scrollViewer.heightInPixels
                && !this._getIsLastMessageVisibleInScrollViewer(chatId);
    }
    _saveScrollPosition(chatId) {
        if (chatId !== this._allMessages.chatId)
            return;
        const firstVisibleMessageBubble = this._getFirstVisibleMessageBubble(chatId);
        if (firstVisibleMessageBubble !== null) {
            const stackPanelHeight = this._controls.chatPanel.stackPanel.heightInPixels;
            const scrollWindowHeight = this._controls.chatPanel.scrollViewer.heightInPixels;
            const scrollValue = this._controls.chatPanel.scrollViewer.verticalValue;
            const scrollWindowTop = (stackPanelHeight - scrollWindowHeight) * scrollValue;
            MessengerPanel._chatScrollPositions.set(chatId, {
                firstVisibleMessageId: firstVisibleMessageBubble.messageId,
                firstVisibleMessageTopPxOnScrollViewer: firstVisibleMessageBubble.controls.rootContainer.topInPixels - scrollWindowTop,
                isLastMessageVisibleInScrollViewer: this._getIsLastMessageVisibleInScrollViewer(chatId),
            });
        }
    }
    _getFirstVisibleMessageBubble(chatId) {
        if (chatId !== this._allMessages.chatId)
            return null;
        const stackPanelHeight = this._controls.chatPanel.stackPanel.heightInPixels;
        const scrollWindowHeight = this._controls.chatPanel.scrollViewer.heightInPixels;
        const scrollValue = this._controls.chatPanel.scrollViewer.verticalValue;
        const scrollWindowTop = (stackPanelHeight - scrollWindowHeight) * scrollValue;
        const scrollWindowBottom = scrollWindowTop + scrollWindowHeight;
        for (let i = 0; i < this._allMessages.items.length; i++) {
            const messageBubble = this._allMessages.items[i];
            const container = messageBubble.controls.rootContainer;
            const containerTop = container.topInPixels;
            if (containerTop >= scrollWindowTop && containerTop <= scrollWindowBottom) {
                return {
                    chatId: messageBubble.chatId,
                    messageId: messageBubble.messageId,
                    sender: messageBubble.sender,
                    // _messageContent: messageBubble._messageContent,
                    controls: messageBubble.controls,
                };
            }
        }
        return null;
    }
    _getIsLastMessageVisibleInScrollViewer(chatId) {
        if (chatId !== this._allMessages.chatId || this._allMessages.items.length === 0)
            return true;
        const stackPanelHeight = this._controls.chatPanel.stackPanel.heightInPixels;
        const scrollWindowHeight = this._controls.chatPanel.scrollViewer.heightInPixels;
        const scrollValue = this._controls.chatPanel.scrollViewer.verticalValue;
        const scrollWindowTop = (stackPanelHeight - scrollWindowHeight) * scrollValue;
        const scrollWindowBottom = scrollWindowTop + scrollWindowHeight;
        const lastMessageBubble = this._allMessages.items[this._allMessages.items.length - 1];
        const container = lastMessageBubble.controls.rootContainer;
        const containerBottom = container.topInPixels + container.heightInPixels;
        return containerBottom >= scrollWindowTop && containerBottom <= scrollWindowBottom;
    }
    outputUnreadMessagesCount(unreadChats) {
        let value = 0;
        const currentChat = this._modelData.chat;
        if (currentChat !== null) {
            const unreadChat = unreadChats.find(x => x.id === currentChat.id);
            if (unreadChat) {
                value = unreadChat.unread_count;
            }
        }
        this._controls.jumpDownControls.unreadMessagesCountContainer.isVisible = value > 0.001;
        const maxUnread = globalOptions.messengerMaxUnreadCount;
        this._controls.jumpDownControls.unreadMessagesCountTextBlock.text = value > maxUnread ? `${maxUnread}+` : value.toString();
    }
    _createContent(parent) {
        const background = this._createBackground();
        parent.addControl(background);
        const rootGrid = GridUtils.CreateGrid("messengerPanelRootGrid", Env.common.messengerPanel.rootGrid, this._mesh2d);
        background.addControl(rootGrid);
        const header = this._createHeader();
        rootGrid.addControl(header.rootContainer, 0);
        const chatPanel = this._createChatPanel();
        rootGrid.addControl(chatPanel.rootContainer, 1);
        chatPanel.warningPopup.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        chatPanel.warningPopup.topInPixels = 0;
        rootGrid.addControl(chatPanel.warningPopup, 1);
        const warningPanel = this._createNoChatsWarningPanel();
        rootGrid.addControl(warningPanel.rootContainer, 1);
        const jumpDownControls = this._createJumpDownPanel();
        rootGrid.addControl(jumpDownControls.rootContainer, 1);
        const tipsPanel = this._createTipsPanel();
        rootGrid.addControl(tipsPanel.rootContainer, 2);
        const quickMessagesPanel = this._createQuickMessagesPanel();
        rootGrid.addControl(quickMessagesPanel.rootContainer, 3);
        const editorPanel = this._createEditorPanel();
        rootGrid.addControl(editorPanel.rootContainer, 4);
        return {
            rootGrid,
            header,
            chatPanel,
            warningPanel,
            jumpDownControls,
            tipsPanel,
            quickMessagesPanel,
            editorPanel,
        };
    }
    _createBackground() {
        const mp = Env.common.messengerPanel;
        const backgroundRectangle = new Rectangle3D("messengerPanelBackgroundRectangle");
        backgroundRectangle.zIndex = -1;
        backgroundRectangle.thickness = 0;
        backgroundRectangle.cornerRadius = this._mesh2d.m2px(mp.background.cornerRadius);
        const backgroundImage = new Image3D("messengerPanelBackgroundImage", mp.background.backgroundImage);
        backgroundImage.zIndex = -1;
        backgroundRectangle.addControl(backgroundImage);
        return backgroundRectangle;
    }
    _createHeader() {
        const mp = Env.common.messengerPanel;
        const h = mp.header;
        const headerGrid = GridUtils.CreateGrid("messengerPanelHeaderGrid", h.headerGrid, this._mesh2d);
        headerGrid.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        headerGrid.background = h.headerGrid.background;
        headerGrid.heightInPixels = this._mesh2d.m2px(h.headerGrid.height);
        const chatListButton = new ImgButton("messengerPanelHeaderChatListButton", h.chatListButton.inactiveImageLayout);
        chatListButton.widthInPixels = this._mesh2d.m2px(h.chatListButton.size);
        chatListButton.heightInPixels = this._mesh2d.m2px(h.chatListButton.size);
        chatListButton.onClickObserver.add(() => {
            setTimeout(() => {
                if (this.isChatListButtonOn) {
                    this._callbacks.hideChatListPanel();
                }
                else {
                    this._callbacks.showChatListPanel();
                }
            });
        });
        headerGrid.addControl(chatListButton, 0, 1);
        const ellipsisButton = new ImgButton("messengerPanelHeaderCloseButton", h.ellipsisButton);
        ellipsisButton.widthInPixels = this._mesh2d.m2px(h.ellipsisButton.size);
        ellipsisButton.heightInPixels = this._mesh2d.m2px(h.ellipsisButton.size);
        ellipsisButton.onClickObserver.add(() => {
            if (this.isVisible)
                this._ellipsisSubmenuHandlers.showSubmenu(SubmenuHandlers.longLifetime);
        });
        headerGrid.addControl(ellipsisButton, 0, 5);
        const closeButton = new ImgButton("messengerPanelHeaderCloseButton", h.closeButton);
        closeButton.widthInPixels = this._mesh2d.m2px(h.closeButton.size);
        closeButton.heightInPixels = this._mesh2d.m2px(h.closeButton.size);
        closeButton.onClickObserver.add(() => this.hide());
        headerGrid.addControl(closeButton, 0, 6);
        const modelSection = this._createModelSection();
        headerGrid.addControl(modelSection.sectionContainer, 0, 3);
        const ellipsisSubmenuItems = this._createEllipsisSubmenu();
        const ellipsisSubmenuFavoritesItem = ellipsisSubmenuItems.find(x => x.name === "Favorites");
        const ellipsisSubmenuGotoRoomItem = ellipsisSubmenuItems.find(x => x.name === "GotoRoom");
        return {
            rootContainer: headerGrid,
            chatListButton,
            modelSection,
            ellipsisButton,
            ellipsisSubmenuFavoritesItem,
            ellipsisSubmenuGotoRoomItem,
        };
    }
    _createModelSection() {
        const mp = Env.common.messengerPanel;
        const h = mp.header;
        const ms = h.modelSection;
        const sectionContainer = new Container("messengerPanelHeaderModelSection");
        sectionContainer.heightInPixels = this._mesh2d.m2px(ms.height);
        const avatarControls = LayoutComponentHelper.createModelProfileControl(this._mesh2d, sectionContainer, ms.avatar);
        avatarControls.rootContainer.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        avatarControls.rootContainer.widthInPixels = avatarControls.outlineContainer.widthInPixels;
        avatarControls.rootContainer.isVisible = false;
        sectionContainer.addControl(avatarControls.rootContainer);
        const bannedAvatar = new Image3D("messengerPanelHeaderBannedAvatar", ms.bannedAvatar.src);
        bannedAvatar.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        bannedAvatar.widthInPixels = this._mesh2d.m2px(ms.bannedAvatar.width);
        bannedAvatar.heightInPixels = this._mesh2d.m2px(ms.bannedAvatar.height);
        bannedAvatar.isVisible = false;
        sectionContainer.addControl(bannedAvatar);
        const topLine = new StackPanel("messengerPanelHeaderTopLine");
        topLine.isVertical = false;
        topLine.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_LEFT;
        topLine.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        topLine.leftInPixels = this._mesh2d.m2px(ms.topLine.left);
        topLine.topInPixels = this._mesh2d.m2px(ms.topLine.top);
        topLine.heightInPixels = this._mesh2d.m2px(ms.topLine.height);
        sectionContainer.addControl(topLine);
        const favoriteStarImage = new Image3D("messengerPanelHeaderFavoritesStar", ms.topLine.star.src);
        favoriteStarImage.widthInPixels = this._mesh2d.m2px(ms.topLine.star.width);
        favoriteStarImage.heightInPixels = this._mesh2d.m2px(ms.topLine.height);
        favoriteStarImage.paddingRightInPixels = this._mesh2d.m2px(ms.topLine.star.paddingRight);
        favoriteStarImage.isVisible = false;
        topLine.addControl(favoriteStarImage);
        const modelNicknameTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "messengerPanelHeaderModelNickname", "", ms.topLine.modelNickname);
        topLine.addControl(modelNicknameTextBlock);
        const statusTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "messengerPanelHeaderStatus", "", ms.status);
        sectionContainer.addControl(statusTextBlock);
        return {
            sectionContainer,
            avatar: {
                rootContainer: avatarControls.rootContainer,
                modelProfileImage: avatarControls.modelProfileImage,
                outlineContainer: avatarControls.outlineContainer,
                statusTextContainer: avatarControls.statusTextContainer,
                statusText: avatarControls.statusText,
            },
            bannedAvatar,
            favoriteStarImage,
            modelNicknameTextBlock,
            statusTextBlock,
        };
    }
    _createEllipsisSubmenu() {
        const mesh = this._ellipsisSubmenuMesh2d;
        MeshUtils.setVisibility(mesh, false);
        const menu = Env.broadcast.menu;
        const sm = Env.common.messengerPanel.header.ellipsisSubmenu;
        const onPointerEnterHandler = () => {
            this._ellipsisSubmenuHandlers.hideSubmenuCancel();
        };
        const onPointerOutHandler = () => {
            this._ellipsisSubmenuHandlers.hideSubmenuStart(SubmenuHandlers.shortLifetime);
        };
        const background = new Rectangle3D("messengerPanelEllipsisSubmenuBackground");
        background.thickness = mesh.m2px(sm.thickness);
        background.background = menu.backgroundColor;
        background.color = sm.borderColor;
        background.cornerRadius = mesh.m2px(menu.cornerRadius);
        background.onPointerEnterObservable.add(onPointerEnterHandler);
        background.onPointerOutObservable.add(onPointerOutHandler);
        mesh.texture.addControl(background);
        const stackPanel = new StackPanel("messengerPanelEllipsisSubmenuStackPanel");
        stackPanel.isHitTestVisible = false;
        stackPanel.isVertical = true;
        stackPanel.ignoreLayoutWarnings = true;
        background.addControl(stackPanel);
        const createItem = (name, caption, callback) => {
            const imgButton = ButtonUtils.CreateImgButton(`messengerPanelEllipsisSubmenuItemButton_${name}`, sm.item.colorLayout, mesh);
            imgButton.onPointerEnterObservable.add(onPointerEnterHandler);
            imgButton.onPointerOutObservable.add(onPointerOutHandler);
            imgButton.onClickObserver.add(() => {
                setTimeout(() => this._ellipsisSubmenuHandlers.hideSubmenuInstantly(), 0);
                callback();
            });
            const textBlock = ObjectBuilder.createTextBlock3D(mesh, `messengerPanelEllipsisSubmenuItemText_${name}`, caption, sm.item.text.textLayout);
            textBlock.stateController.setStates(sm.item.text.textLayout.states.map(s => {
                return { state: s.state, style: FontUtils.getAdjustedFontStyle(s.style, mesh) };
            }));
            imgButton.addControl(textBlock);
            return { name, imgButton, textBlock };
        };
        const result = [
            // createItem("ViewProfile", text.common_messenger_ellipsis_menu_view_profile, () => {
            //     this._callbacks.viewModelProfile();
            // }),
            // createItem("PrivateShowOptions", text.common_messenger_ellipsis_menu_private_show_options, () => {
            //     this._callbacks.showPrivateShowOptions();
            // }),
            createItem("Favorites", text.common_messenger_ellipsis_menu_add_to_favorites, () => {
                this._switchIsFavoriteModel();
            }),
            createItem("GotoRoom", text.common_messenger_ellipsis_menu_goto_room, () => {
                const modelNickname = this._modelData.modelNickname;
                if (modelNickname !== null) {
                    this.hide();
                    setTimeout(async () => {
                        await this._callbacks.gotoLiveRoomAsync(modelNickname);
                    });
                }
            }),
        ];
        result.forEach(x => stackPanel.addControl(x.imgButton));
        return result;
    }
    _createChatPanel() {
        const cp = Env.common.messengerPanel.chat;
        const scrollViewer = new ChatScrollViewer("messengerPanelChatScrollViewer");
        scrollViewer.isVisible = false;
        scrollViewer.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        scrollViewer.color = cp.scrollView.colors.background;
        scrollViewer.barColor = cp.scrollView.colors.bar;
        scrollViewer.hoverBarColor = cp.scrollView.colors.hoverBar;
        scrollViewer.pressedBarColor = cp.scrollView.colors.pressedBar;
        scrollViewer.verticalBarPaddingInPixels = {
            left: this._mesh2d.m2px(cp.scrollView.verticalBar.padding.left),
            right: this._mesh2d.m2px(cp.scrollView.verticalBar.padding.right),
            top: this._mesh2d.m2px(cp.scrollView.verticalBar.padding.top),
            bottom: this._mesh2d.m2px(cp.scrollView.verticalBar.padding.bottom)
        };
        scrollViewer.barSizeInPixels = this._mesh2d.m2px(cp.scrollView.verticalBar.size);
        this._onVerticalValueChangedObserver = scrollViewer.onVerticalValueChangedObservable.add(this._verticalValueChangedHandler);
        const scrollHandler = new ScrollHandler([this._mesh2d.mesh.name], this._mesh2d.m2px(Env.broadcast.menu.chat.scrollView.speed), (v) => {
            const deltaHeight = scrollViewer.children[0].heightInPixels - scrollViewer.heightInPixels;
            scrollViewer.verticalValue = deltaHeight === 0 ? 0 : Math.max(Math.min(scrollViewer.verticalValue + v / deltaHeight, 1), 0);
        });
        const pointerAddon = XRInputHelper.addPointerAddon(this._mesh2d);
        pointerAddon.onPointerOut.add(() => scrollHandler.stopScroll());
        const stackPanel = this._createChatStackPanel();
        scrollViewer.addControl(stackPanel);
        const warningPopup = new MenuPopupContainer("messengerPanelChatWarningPopup", this._mesh2d, cp.warningPopup);
        warningPopup.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        warningPopup.hide();
        return {
            rootContainer: scrollViewer,
            scrollViewer,
            scrollHandler,
            stackPanel,
            topGap: null,
            bottomGap: null,
            warningPopup,
        };
    }
    _createChatStackPanel() {
        const stackPanel = new StackPanel("messengerPanelChatStackPanel");
        stackPanel.ignoreLayoutWarnings = true;
        stackPanel.isVertical = true;
        stackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_BOTTOM;
        stackPanel.adaptHeightToChildren = true;
        stackPanel.spacing = this._mesh2d.m2px(Env.common.messengerPanel.chat.chatStackPanel.spacing);
        return stackPanel;
    }
    _tryToMarkUnreadMessages(scrollValue) {
        const childHeight = this._controls.chatPanel.stackPanel.heightInPixels;
        const scrollWindowHeight = this._controls.chatPanel.scrollViewer.heightInPixels;
        const scrollWindowTop = (childHeight - scrollWindowHeight) * scrollValue;
        const scrollWindowBottom = scrollWindowTop + scrollWindowHeight;
        for (const unreadMessage of this._unreadModelMessages) {
            const bubble = unreadMessage.controls.rootContainer;
            const bottom = bubble.topInPixels + bubble.heightInPixels;
            const inWindow = bottom >= scrollWindowTop && bottom <= scrollWindowBottom;
            if (!unreadMessage.wasShownToUser && inWindow) {
                unreadMessage.wasShownToUser = true;
                if (this._markAsReadTimeout === null) {
                    this._markAsReadTimeout = setTimeout(async () => {
                        await this._markAsReadHandlerAsync();
                    }, MARK_AS_READ_DELAY);
                }
            }
        }
    }
    _canLoadHistoryMessages(scrollValue) {
        const childHeight = this._controls.chatPanel.stackPanel.heightInPixels;
        const scrollWindowHeight = this._controls.chatPanel.scrollViewer.heightInPixels;
        const scrollWindowTop = (childHeight - scrollWindowHeight) * scrollValue;
        const scrollWindowBottom = scrollWindowTop + scrollWindowHeight;
        if (childHeight > scrollWindowHeight) {
            const topGap = this._controls.chatPanel.topGap;
            if (topGap === null)
                return false;
            const topGapBottomPx = topGap.topInPixels + topGap.heightInPixels;
            if (topGapBottomPx >= scrollWindowTop && topGapBottomPx <= scrollWindowBottom) {
                return true;
            }
        }
        return false;
    }
    _tryToLoadHistoryMessages(scrollValue) {
        if (this._canLoadHistoryMessages(scrollValue)) {
            setTimeout(async () => {
                if (this._canLoadHistoryMessages(this._controls.chatPanel.scrollViewer.verticalValue)) {
                    if (this._modelData.chat !== null) {
                        const historyChunk = await this._messengerDataCallbacks.loadChatHistoryAsync(this._modelData.chat.id);
                        if (historyChunk.length > 0) {
                            this._messengerDataCallbacks.outputMessages(this._modelData.chat.id);
                        }
                    }
                }
            }, 500); // After 500 milliseconds topGap is still visible => we can load history.
        }
    }
    async _markAsReadHandlerAsync() {
        const unmarkedMessages = this._unreadModelMessages.filter(x => x.wasShownToUser && !x.wasMarkedAsRead);
        if (unmarkedMessages.length === 0)
            return;
        const latestMessage = unmarkedMessages.reduce((latest, item) => {
            return item.message.sent_at > latest.message.sent_at ? item : latest;
        }, unmarkedMessages[0]);
        await this._messengerDataCallbacks.readMessageAsync(latestMessage.chatId, latestMessage.message.id);
        latestMessage.wasMarkedAsRead = true;
        this._markAsReadTimeout = null;
    }
    _createNoChatsWarningPanel() {
        const cp = Env.common.messengerPanel.chat;
        const rootContainer = new Container("messengerPanelNoChatsWarningRoot");
        rootContainer.isVisible = false;
        const caption = ObjectBuilder.createTextBlock3D(this._mesh2d, "messengerPanelNoChatsWarningCaption", text.common_messenger_warning_no_messages_caption, cp.warning.caption);
        rootContainer.addControl(caption);
        const description = ObjectBuilder.createTextBlock3D(this._mesh2d, "messengerPanelNoChatsWarningDescription", text.common_messenger_warning_no_messages_description, cp.warning.description);
        rootContainer.addControl(description);
        return {
            rootContainer,
        };
    }
    _createJumpDownPanel() {
        const jd = Env.common.messengerPanel.chat.jumpDown;
        const rootContainer = new Container("messengerPanelJumpDownContainer");
        rootContainer.zIndex = 1;
        rootContainer.widthInPixels = this._mesh2d.m2px(jd.width);
        rootContainer.heightInPixels = this._mesh2d.m2px(jd.height);
        rootContainer.horizontalAlignment = Container.HORIZONTAL_ALIGNMENT_RIGHT;
        rootContainer.verticalAlignment = Container.VERTICAL_ALIGNMENT_BOTTOM;
        rootContainer.leftInPixels = -this._mesh2d.m2px(jd.right);
        rootContainer.topInPixels = -this._mesh2d.m2px(jd.bottom);
        const jumpDownButton = new ImgButton("messengerPanelJumpDownButton", jd.button);
        jumpDownButton.verticalAlignment = Container.VERTICAL_ALIGNMENT_BOTTOM;
        const diameterInPixels = this._mesh2d.m2px(jd.width);
        jumpDownButton.widthInPixels = diameterInPixels;
        jumpDownButton.heightInPixels = diameterInPixels;
        jumpDownButton.children[0].detectPointerOnOpaqueOnly = false;
        rootContainer.addControl(jumpDownButton);
        jumpDownButton.onClickObserver.add(() => {
            this._controls.chatPanel.scrollViewer.verticalValue = 1;
        });
        const unreadMessagesCountContainer = new Rectangle3D("messengerPanelJumpDownUnreadsBackground");
        unreadMessagesCountContainer.zIndex = 2;
        unreadMessagesCountContainer.horizontalAlignment = Rectangle3D.HORIZONTAL_ALIGNMENT_CENTER;
        unreadMessagesCountContainer.verticalAlignment = Rectangle3D.VERTICAL_ALIGNMENT_TOP;
        unreadMessagesCountContainer.adaptWidthToChildren = true;
        unreadMessagesCountContainer.heightInPixels = this._mesh2d.m2px(jd.unreads.background.height);
        unreadMessagesCountContainer.cornerRadius = this._mesh2d.m2px(jd.unreads.background.cornerRadius);
        unreadMessagesCountContainer.thickness = 0;
        unreadMessagesCountContainer.useArc = true;
        unreadMessagesCountContainer.background = jd.unreads.background.color;
        rootContainer.addControl(unreadMessagesCountContainer);
        const unreadMessagesCountTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, "messengerPanelJumpDownUnreadsCountTextBlock", "", jd.unreads.textLayout);
        unreadMessagesCountTextBlock.zIndex = 2;
        unreadMessagesCountContainer.addControl(unreadMessagesCountTextBlock);
        unreadMessagesCountContainer.isVisible = false;
        return {
            rootContainer,
            unreadMessagesCountContainer,
            unreadMessagesCountTextBlock,
        };
    }
    _createTipsPanel() {
        const ti = Env.common.messengerPanel.tips;
        const rootContainer = new Container("messengerPanelTipsRoot");
        rootContainer.heightInPixels = this._mesh2d.m2px(ti.height);
        const separator = new Container("messengerPanelTipsSeparator");
        separator.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        separator.widthInPixels = this._mesh2d.m2px(ti.separator.width);
        separator.heightInPixels = this._mesh2d.m2px(ti.separator.height);
        separator.background = ti.separator.color;
        rootContainer.addControl(separator);
        const valuesStackPanel = new StackPanel("messengerPanelTipsValuesStackPanel");
        valuesStackPanel.ignoreLayoutWarnings = true;
        valuesStackPanel.isVertical = false;
        valuesStackPanel.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        valuesStackPanel.topInPixels = this._mesh2d.m2px(ti.valuesStackPanel.top);
        valuesStackPanel.heightInPixels = this._mesh2d.m2px(ti.valuesStackPanel.height);
        valuesStackPanel.spacing = this._mesh2d.m2px(ti.valuesStackPanel.spacing);
        rootContainer.addControl(valuesStackPanel);
        const cs = Env.broadcast.menu.chat;
        const sendTipAsync = async (tipValue) => {
            if (!AppView.getInstance().appController.messengerSdk.authManager.isAuthorized)
                return;
            if (this._modelData.selectedModelId === null)
                return;
            try {
                await this._messengerDataCallbacks.sendTipsAsync(this._modelData.selectedModelId, tipValue);
                if (tipValue >= globalOptions.tipsLevel1AnimationStartsAt && tipValue <= globalOptions.tipsLevel1AnimationEndsAt) {
                    AppView.getInstance().animationView.showAnimation("hearts3");
                }
                else if (tipValue >= globalOptions.tipsLevel2AnimationStartsAt && tipValue <= globalOptions.tipsLevel2AnimationEndsAt) {
                    AppView.getInstance().animationView.showAnimation("hearts5");
                }
                else if (tipValue >= globalOptions.tipsLevel3AnimationStartsAt) {
                    AppView.getInstance().animationView.showAnimation("hearts8");
                }
                if (tipValue >= globalOptions.tipsCoinsAnimationStartsAt) {
                    AppView.getInstance().animationView.showAnimation("coins");
                }
            }
            catch (e) {
                console.log(e);
                if (e.code === 406) {
                    this._controls.chatPanel.warningPopup.show(text.broadcast_menu_warning_notEnoughTokens);
                }
            }
        };
        for (const tipValue of cs.sendTips.tips.values) {
            const tipButton = ButtonUtils.CreateImgButtonWithText(`messengerPanelTipsTipButton_${tipValue}`, {
                imageLayout: cs.sendTips.tips.imageLayout,
                textLayout: cs.sendTips.tips.textLayout,
                text: `${tipValue}`,
            }, this._mesh2d);
            tipButton.onClickObserver.add(async () => {
                await sendTipAsync(tipValue);
            });
            valuesStackPanel.addControl(tipButton);
        }
        const tipsInputControl = InputUtils.CreateInput("messengerPanelTipsInput", ti.customTip.input, this._mesh2d);
        tipsInputControl.placeholder = text.broadcast_menu_chat_customTip;
        if (Settings.getInstance().broadcast.customTipsAmount) {
            tipsInputControl.text = Settings.getInstance().broadcast.customTipsAmount.toString();
        }
        tipsInputControl.onPointerClickObservable.add(() => {
            setTimeout(() => {
                if (this._tipsKeyboard === null) {
                    this._tipsKeyboard = new BroadcastMenuTipsKeyboard(this._tipsKeyboardMesh2d, () => { tipsInputControl.blur(); }, tipsInputControl);
                }
                this._tipsKeyboard.show();
                const ct = Env.common.messengerPanel.tips.customTip;
                const x = this._mesh2d.px2m(tipsInputControl._currentMeasure.left) - this._mesh2d.width / 2 + ct.tipsKeyboard.width / 2;
                const y = this._mesh2d.height / 2 + ct.tipsKeyboard.height / 2 - this._mesh2d.px2m(tipsInputControl._currentMeasure.top) + ct.tipsKeyboard.offset;
                this._tipsKeyboard.mesh.mesh.position = new Vector3(x, y, -0.02);
            });
        });
        tipsInputControl.onTextChangedObservable.add(() => {
            let changedText = tipsInputControl.text;
            if (changedText.length > cs.sendTips.customTip.input.maxLength) {
                changedText = changedText.substring(0, changedText.length - 1);
            }
            while (!changedText.match(cs.sendTips.customTip.input.filter)) {
                changedText = changedText.replace(cs.sendTips.customTip.input.substitution, "");
            }
            tipsInputControl.text = changedText;
            const safeParseInt = (text, defaultValue) => {
                const result = Number.parseInt(text);
                return isNaN(result) ? defaultValue : result;
            };
            AppView.getInstance().customTip = safeParseInt(tipsInputControl.text, 0);
        });
        valuesStackPanel.addControl(tipsInputControl);
        const sendCustomTipButton = ButtonUtils.CreateImgButton("messengerPanelTipsSendCustomTipButton", cs.sendTips.customTip.sendButton, this._mesh2d);
        sendCustomTipButton.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        sendCustomTipButton.isPointerBlocker = true;
        sendCustomTipButton.onClickObserver.add(async () => {
            const tipValue = Number.parseInt(tipsInputControl.text);
            Settings.getInstance().broadcast.customTipsAmount = tipValue;
            await sendTipAsync(tipValue);
        });
        tipsInputControl.addControl(sendCustomTipButton);
        return {
            rootContainer,
            tipsInputControl,
        };
    }
    _createQuickMessagesPanel() {
        const qm = Env.common.messengerPanel.quickMessages;
        const rootContainer = new Container("messengerPanelQuickMessagesRoot");
        rootContainer.heightInPixels = this._mesh2d.m2px(qm.height);
        const stackPanel = new StackPanel("messengerPanelQuickMessagesStackPanel");
        stackPanel.ignoreLayoutWarnings = true;
        stackPanel.isVertical = false;
        stackPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        stackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        stackPanel.heightInPixels = this._mesh2d.m2px(qm.stackPanel.height);
        stackPanel.leftInPixels = this._mesh2d.m2px(qm.stackPanel.left);
        stackPanel.spacing = this._mesh2d.m2px(qm.stackPanel.spacing);
        rootContainer.addControl(stackPanel);
        text.common_messenger_quick_messages.forEach((messageText, index) => {
            const itemButton = ButtonUtils.CreateImgButton(`messengerPanelQuickMessage${index}`, qm.item.buttonLayout, this._mesh2d);
            itemButton.adaptWidthToChildren = true;
            stackPanel.addControl(itemButton);
            const messageTextBlock = ObjectBuilder.createTextBlock3D(this._mesh2d, `messengerPanelQuickMessageText${index}`, messageText, qm.item.textLayout);
            itemButton.addControl(messageTextBlock);
            //setTimeout(() => {
            //    const textWidthPx = ContextUtils.MeasureText(messageTextBlock);
            //    const paddingLeftPx = this._mesh2d.m2px(qm.item.textLayout.padding?.left ?? 0);
            //    const paddingRightPx = this._mesh2d.m2px(qm.item.textLayout.padding?.right ?? 0);
            //    itemButton.widthInPixels = textWidthPx + paddingLeftPx + paddingRightPx;
            //});
            itemButton.onClickObserver.add(_ev => {
                let previousText = this._controls.editorPanel.inputControl.text;
                if (previousText.length > 0 && previousText === previousText.trimEnd())
                    previousText += " ";
                this._controls.editorPanel.inputControl.text = previousText + messageText;
            });
        });
        return {
            rootContainer,
        };
    }
    _createEditorPanel() {
        const rootContainer = new Container("messengerPanelEditorRoot");
        rootContainer.adaptHeightToChildren = true;
        const cs = Env.common.messengerPanel.chat;
        const inactiveInputStubSeparator = new Container("inactiveInputStubSeparator");
        inactiveInputStubSeparator.verticalAlignment = Container.VERTICAL_ALIGNMENT_TOP;
        inactiveInputStubSeparator.widthInPixels = this._mesh2d.m2px(cs.inactiveInputStub.separator.width);
        inactiveInputStubSeparator.heightInPixels = this._mesh2d.m2px(cs.inactiveInputStub.separator.height);
        inactiveInputStubSeparator.background = cs.inactiveInputStub.separator.color;
        inactiveInputStubSeparator.zIndex = 1;
        rootContainer.addControl(inactiveInputStubSeparator);
        const inactiveInputStub = new Rectangle3D("inactiveInputStub");
        inactiveInputStub.paddingTopInPixels = this._mesh2d.m2px(cs.inactiveInputStub.paddingTop);
        inactiveInputStub.thickness = 0;
        inactiveInputStub.widthInPixels = this._mesh2d.m2px(cs.inactiveInputStub.width);
        inactiveInputStub.adaptHeightToChildren = true;
        inactiveInputStub.background = cs.inactiveInputStub.background;
        inactiveInputStub.cornerRadius = this._mesh2d.m2px(cs.inactiveInputStub.cornerRadius);
        inactiveInputStub.isVisible = false;
        rootContainer.addControl(inactiveInputStub);
        const inactiveInputStubImage = new Image3D("inactiveInputStubImage", cs.inactiveInputStub.lockImg.src);
        inactiveInputStubImage.horizontalAlignment = Image3D.HORIZONTAL_ALIGNMENT_LEFT;
        inactiveInputStubImage.verticalAlignment = Image3D.VERTICAL_ALIGNMENT_BOTTOM;
        inactiveInputStubImage.widthInPixels = this._mesh2d.m2px(cs.inactiveInputStub.lockImg.width);
        inactiveInputStubImage.heightInPixels = this._mesh2d.m2px(cs.inactiveInputStub.lockImg.height);
        inactiveInputStubImage.paddingTopInPixels = this._mesh2d.m2px(cs.inactiveInputStub.lockImg.paddingTop);
        inactiveInputStubImage.leftInPixels = this._mesh2d.m2px(cs.inactiveInputStub.lockImg.left);
        inactiveInputStubImage.topInPixels = this._mesh2d.m2px(cs.inactiveInputStub.lockImg.top);
        inactiveInputStub.addControl(inactiveInputStubImage);
        const inactiveInputStubText = ObjectBuilder.createTextBlock3D(this._mesh2d, "inactiveInputStubText", "", cs.inactiveInputStub.text);
        inactiveInputStub.addControl(inactiveInputStubText);
        const inputControl = InputUtils.CreateInput("input", cs.input, this._mesh2d);
        let isInputFocused = false;
        const keyboardOwnerId = "messengerPanel";
        inputControl.leftInPixels = this._mesh2d.m2px(cs.input.left);
        inputControl.placeholder = text.common_messenger_input_placeholder;
        rootContainer.addControl(inputControl);
        inputControl.onPointerDownObservable.add(() => {
            inputControl.focus();
        });
        inputControl.onFocusObservable.add(() => {
            isInputFocused = true;
            this._keyboard.setActiveOwner(keyboardOwnerId);
            this._keyboard.isVisible = true;
            inputControl.isCursorEnabled = true;
        });
        inputControl.onBlurObservable.add(() => {
            isInputFocused = false;
        });
        this._keyboard.control.connect(inputControl);
        inputControl.connectControl(this._keyboard.rootControl);
        this._keyboard.onEnterButtonClickedObservable.add(async () => {
            if (!isInputFocused && this._keyboard.lastActiveOwner !== keyboardOwnerId)
                return;
            if (!isInputFocused) {
                inputControl.focus();
            }
            await this._sendMessageAsync();
        });
        const sendButton = ButtonUtils.CreateImgButton("send", cs.send, this._mesh2d);
        sendButton.isPointerBlocker = true;
        sendButton.onPointerClickObservable.add(async () => {
            await this._sendMessageAsync();
        });
        sendButton.isVisible = false;
        inputControl.addControl(sendButton);
        inputControl.onTextChangedObservable.add(async () => {
            if (this._modelData.selectedModelId === null)
                return;
            const text = inputControl.text;
            const isVisible = text.length > 0;
            sendButton.isVisible = isVisible;
            if (this._modelData.chat !== null) {
                if (isVisible) {
                    this._messengerDataCallbacks.tryToSendTyping(this._modelData.chat);
                }
                this._messengerDataCallbacks.sendDraftMessage(this._modelData.chat, text);
            }
        });
        inputControl.onDirtyObservable.add(() => {
            setTimeout(() => {
                const chatScrollViewer = this._controls.chatPanel.scrollViewer;
                const curVal = chatScrollViewer.verticalValue;
                chatScrollViewer.verticalValue = 0;
                chatScrollViewer.verticalValue = curVal;
                this._controls.rootGrid.markAsDirty();
            }, 0);
        });
        this._keyboard.control.onKeyPressObservable.add(async (key) => {
            if (!isInputFocused && this._keyboard.lastActiveOwner !== keyboardOwnerId)
                return;
            if (key === Keys.enter) {
                if (!isInputFocused) {
                    inputControl.focus();
                }
                await this._sendMessageAsync();
            }
        });
        return {
            rootContainer,
            inputControl,
            sendButton,
            inactiveInputStub: {
                rootContainer: inactiveInputStub,
                separator: inactiveInputStubSeparator,
                textBlock: inactiveInputStubText,
            },
        };
    }
    async _sendMessageAsync() {
        const inputControl = this._controls.editorPanel.inputControl;
        const sendButton = this._controls.editorPanel.sendButton;
        if (inputControl.text.trim() !== "" && this._modelData.selectedModelId !== null) {
            sendButton.isEnabled = false;
            try {
                if (this._modelData.chat === null) {
                    await this._messengerDataCallbacks.createChatAsync(this._modelData.selectedModelId);
                    // (this._modelData.chat was updated)
                }
                if (this._modelData.chat !== null) {
                    MessengerPanel._chatScrollPositions.delete(this._modelData.chat.id);
                    const sendResult = await this._messengerDataCallbacks.sendMessageAsync(this._modelData.chat, inputControl.text);
                    if (sendResult !== null) {
                        inputControl.text = "";
                    }
                    this._controls.chatPanel.scrollViewer.verticalValue = 1;
                }
            }
            finally {
                sendButton.isEnabled = true;
            }
        }
    }
    _updateIsFavoriteMenuItem(params) {
        this._controls.header.ellipsisSubmenuFavoritesItem.imgButton.isEnabled = true;
        this._controls.header.ellipsisSubmenuFavoritesItem.textBlock.text = params.checked
            ? text.common_messenger_ellipsis_menu_remove_from_favorites
            : text.common_messenger_ellipsis_menu_add_to_favorites;
    }
    _updateGotoRoomMenuItem(params) {
        this._controls.header.ellipsisSubmenuGotoRoomItem.imgButton.isEnabled =
            params.selectedModelId !== null &&
                (params.pinnedModelId === null ||
                    (params.selectedModelId !== params.pinnedModelId &&
                        params.pinnedBroadcastManagerState !== BroadcastManagerState.MY_PRIVATE_SHOW &&
                        params.pinnedBroadcastManagerState !== BroadcastManagerState.SPY_SHOW));
    }
    _updateHeader() {
        const statusData = AvatarStatus.broadcastApiStatusMapForMessenger[this._modelData.broadcastApiStatus];
        this._controls.header.modelSection.avatar.outlineContainer.color = statusData.color;
        this._controls.header.modelSection.avatar.statusTextContainer.background = statusData.color;
        this._controls.header.modelSection.avatar.statusText.text = statusData.text;
        this._controls.header.modelSection.avatar.rootContainer.isVisible = !this._modelData.isBanned;
        this._controls.header.modelSection.bannedAvatar.isVisible = this._modelData.isBanned;
        this._controls.header.modelSection.favoriteStarImage.isVisible = this._modelData.isFavorite;
        this._controls.header.modelSection.modelNicknameTextBlock.text = this._modelData.modelNickname ?? "";
        const src = this._modelData.modelProfileImageUrl;
        this._controls.header.modelSection.avatar.modelProfileImage.setSource(src, Env.lobby.gallery.defaultPreview.path);
        this._controls.header.modelSection.avatar.statusTextContainer.isVisible = true;
        let statusText;
        if (this._modelData.isBanned) {
            statusText = text.common_messenger_model_status_banned_user;
        }
        else if (this._modelData.isTyping) {
            statusText = text.common_messenger_model_status_typing;
        }
        else if (this._modelData.broadcastApiStatus === BroadcastApiStatus.OFFLINE && this._modelData.lastBroadcast) {
            statusText = DateUtils.getModelAbsenceTime({
                lastBroadcastDate: this._modelData.lastBroadcast,
                minutesTemplateString: text.common_messenger_model_status_offline_delay_in_minutes,
                hoursTemplateString: text.common_messenger_model_status_offline_delay_in_hours,
                daysTemplateString: text.common_messenger_model_status_offline_delay_in_days,
                monthsTemplateString: text.common_messenger_model_status_offline_delay_in_months,
            });
        }
        else {
            statusText = text.common_messenger_model_status_online;
        }
        this._controls.header.modelSection.statusTextBlock.text = statusText;
        this._controls.header.ellipsisButton.isVisible = !this._modelData.isBanned;
        this._updateIsFavoriteMenuItem({
            checked: this._modelData.isFavorite,
            modelId: this._modelData.selectedModelId,
        });
        this._updateGotoRoomMenuItem({
            selectedModelId: this._modelData.selectedModelId,
            pinnedModelId: this._modelData.pinnedModelId,
            pinnedBroadcastManagerState: this._modelData.pinnedBroadcastManagerState,
        });
        this._updateModelSectionWidth();
    }
    setInputControlsAvailability(params) {
        if (params.isMuted && this._keyboard.isVisible) {
            this._keyboard.isVisible = false;
        }
        const tipsVisible = !params.isBanned;
        const quickMessagesVisible = !params.isMuted && !params.isBanned;
        const showStub = params.isMuted || params.isBanned;
        const componentVisibilityChanged = this._controls.tipsPanel.rootContainer.isVisible !== tipsVisible
            || this._controls.quickMessagesPanel.rootContainer.isVisible !== quickMessagesVisible
            || this._controls.editorPanel.inactiveInputStub.separator.isVisible !== showStub;
        this._controls.tipsPanel.rootContainer.isVisible = tipsVisible;
        this._controls.tipsPanel.rootContainer.heightInPixels = tipsVisible
            ? this._mesh2d.m2px(Env.common.messengerPanel.tips.height)
            : 0;
        this._controls.quickMessagesPanel.rootContainer.isVisible = quickMessagesVisible;
        this._controls.quickMessagesPanel.rootContainer.heightInPixels = quickMessagesVisible
            ? this._mesh2d.m2px(Env.common.messengerPanel.quickMessages.height)
            : 0;
        this._controls.rootGrid.markAllAsDirty();
        this._controls.editorPanel.inputControl.isEnabled = !showStub;
        this._controls.editorPanel.inputControl.isVisible = !showStub;
        this._controls.editorPanel.inactiveInputStub.rootContainer.isVisible = showStub;
        this._controls.editorPanel.inactiveInputStub.separator.isVisible = showStub;
        this._controls.editorPanel.inactiveInputStub.textBlock.text = params.isMuted
            ? text.common_messenger_input_placeholder_muted
            : text.common_messenger_input_placeholder_banned;
        //DCVR-6758 fix - force scroll redraw:
        if (componentVisibilityChanged) {
            const previousScrollValue = this._controls.chatPanel.scrollViewer.verticalValue;
            if (previousScrollValue > 0.99) {
                setTimeout(() => {
                    this._controls.chatPanel.scrollViewer.verticalValue = 0.5;
                    setTimeout(() => {
                        this._controls.chatPanel.scrollViewer.verticalValue = previousScrollValue;
                    }, 10);
                }, 10);
            }
        }
    }
    _updateModelSectionWidth() {
        const ms = Env.common.messengerPanel.header.modelSection;
        const maxModelSectionWidthPx = this._mesh2d.m2px(ms.maxWidth);
        const topLineLeftPx = this._mesh2d.m2px(ms.topLine.left);
        const isFavoriteWidthPx = this._mesh2d.m2px(this._modelData.isFavorite ? ms.topLine.star.width : 0);
        const desiredNicknameWidthPx = ContextUtils.MeasureText(this._controls.header.modelSection.modelNicknameTextBlock);
        const maxNicknameWidthPx = maxModelSectionWidthPx - topLineLeftPx - isFavoriteWidthPx;
        const nicknameWidthPx = Math.min(desiredNicknameWidthPx, maxNicknameWidthPx);
        const desiredStatusWidthPx = ContextUtils.MeasureText(this._controls.header.modelSection.statusTextBlock);
        const maxStatusWidthPx = maxModelSectionWidthPx - topLineLeftPx;
        const statusWidthPx = Math.min(desiredStatusWidthPx, maxStatusWidthPx);
        this._controls.header.modelSection.modelNicknameTextBlock.widthInPixels = nicknameWidthPx;
        this._controls.header.modelSection.statusTextBlock.widthInPixels = statusWidthPx;
        this._controls.header.modelSection.sectionContainer.widthInPixels = Math.max(topLineLeftPx + isFavoriteWidthPx + nicknameWidthPx, topLineLeftPx + statusWidthPx);
        this._controls.header.modelSection.modelNicknameTextBlock.textWrapping =
            desiredNicknameWidthPx > maxNicknameWidthPx ? TextWrapping.Ellipsis : TextWrapping.Clip;
        this._controls.header.modelSection.statusTextBlock.textWrapping =
            desiredStatusWidthPx > maxStatusWidthPx ? TextWrapping.Ellipsis : TextWrapping.Clip;
    }
    _setImageSource(image, url) {
        const fallback = Env.lobby.gallery.defaultPreview.path;
        const source = url ?? fallback;
        const img = image;
        if (image instanceof Image3D)
            img.setSource(source, fallback);
        else
            img.source = source;
    }
    _refreshModelAvatar(modelId, url) {
        const avatarImages = this._avatars.get(modelId);
        if (!avatarImages || avatarImages.length === 0)
            return;
        const busted = url;
        const base = avatarImages[0];
        base.image.onImageLoadedObservable.addOnce((image) => {
            const aspectRatio = image.domImage.width / image.domImage.height;
            base.image.widthInPixels = base.image.heightInPixels * aspectRatio;
            const modelImages = this._avatars.get(modelId);
            modelImages?.forEach(avatarImage => {
                if (avatarImage.widthInPixels === undefined)
                    avatarImage.widthInPixels = base.image.widthInPixels;
                if (avatarImage.heightInPixels === undefined)
                    avatarImage.heightInPixels = base.image.heightInPixels;
            });
            setTimeout(() => {
                this._controls.chatPanel.stackPanel.markAsDirty();
                this._controls.chatPanel.scrollViewer.markAsDirty(true);
                this._controls.rootGrid.markAllAsDirty();
            }, 0);
        });
        this._setImageSource(base.image, busted);
        for (let i = 1; i < avatarImages.length; i++)
            this._setImageSource(avatarImages[i].image, busted);
    }
    _setChatPanelMode(mode) {
        this._controls.chatPanel.rootContainer.isVisible = mode === "chat";
        this._controls.warningPanel.rootContainer.isVisible = mode === "warning";
    }
    _switchIsFavoriteModel() {
        setTimeout(async () => {
            const modelId = this._modelData.selectedModelId;
            const isFavorite = this._modelData.isFavorite;
            if (modelId === null)
                return;
            this._controls.header.ellipsisSubmenuFavoritesItem.imgButton.isEnabled = false;
            try {
                await this._messengerDataCallbacks.setModelIsFavoriteAsync(modelId, !isFavorite);
            }
            catch (e) {
                this._controls.header.ellipsisSubmenuFavoritesItem.imgButton.isEnabled = true;
                console.error(e.message);
            }
        });
    }
}
MessengerPanel._chatScrollPositions = new Map(); // (key: chatId)
